import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  getBoardingSummary,
  getAuthorityOutcomes,
  getFacilityStatus,
  getSpvSoldServicing,
  getEvidenceExportHistory,
} from '../../api/originatorApi';
import { DashboardHeader } from '../../components/reports/DashboardHeader';
import { FilterToolbar } from '../../components/reports/FilterToolbar';
import { MetricsCards } from '../../components/reports/MetricsCards';
import { DetailGrid } from '../../components/reports/DetailGrid';
import { ExportHistorySection } from '../../components/reports/ExportHistorySection';
import { ErrorBanner } from '../../components/ui/ErrorBanner';

// One config per originator report route. All render from the same layout.
const REPORTS = {
  '/reports/boarding': {
    title: 'Boarding Summary Dashboard',
    subtitle: 'Submission throughput, package health, and cure SLAs.',
    fetcher: getBoardingSummary,
    trendTitle: 'Boarding trend',
  },
  '/reports/authority': {
    title: 'Authority Outcomes Dashboard',
    subtitle: 'Admitted, repository-only, rejected, and custodian-pending outcomes.',
    fetcher: getAuthorityOutcomes,
    trendTitle: 'Authority trend',
  },
  '/reports/custody': {
    title: 'Custody / Facility Status Dashboard',
    subtitle: 'Vault, pledged, release pending, transfers, and facility-tied positions.',
    fetcher: getFacilityStatus,
    trendTitle: 'Custody trend',
  },
  '/reports/spv-servicing': {
    title: 'SPV / Sold with Servicing Dashboard',
    subtitle: 'Loans visible after sale because servicing is retained.',
    fetcher: getSpvSoldServicing,
    trendTitle: 'Servicing trend',
  },
  '/reports/evidence': {
    title: 'Evidence & Export History Dashboard',
    subtitle: 'Bundles, receipts, certified copies, and report exports.',
    fetcher: getEvidenceExportHistory,
    trendTitle: 'Evidence trend',
    showExports: true,
    hideMetrics: true,
  },
};

export function OriginatorReportDashboardPage() {
  const { pathname } = useLocation();
  const config = REPORTS[pathname];

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!config) return undefined;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await config.fetcher();
        if (!cancelled) setPayload(response);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Unable to load dashboard.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // pathname drives which fetcher runs; reloadToken triggers retries.
  }, [pathname, reloadToken, config]);

  if (!config) {
    return (
      <p className="text-sm text-umb-error">
        Unknown report.{' '}
        <Link to="/reports" className="font-medium underline">
          Back to reports
        </Link>
      </p>
    );
  }

  return (
    <div className="space-y-5">
      <section className="overflow-hidden rounded-2xl border border-eorig-border bg-white shadow-eorig-card">
        <DashboardHeader title={config.title} subtitle={config.subtitle} />

        <FilterToolbar filters={payload?.filters} />

        {error ? (
          <div className="p-5 sm:p-6">
            <ErrorBanner message={error} onRetry={() => setReloadToken((t) => t + 1)} />
          </div>
        ) : null}

        {config.hideMetrics ? null : (
          <MetricsCards metrics={payload?.metrics} loading={loading} />
        )}

        <div className="p-5 lg:p-6">
          <DetailGrid items={payload?.detail_grid} />
        </div>

        {config.showExports ? <ExportHistorySection /> : null}
      </section>

      <Link to="/reports" className="inline-flex text-sm font-semibold text-eorig-navy-600 hover:text-eorig-cyan">
        ← Back to Reports center
      </Link>
    </div>
  );
}
